import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

import type { Page } from '../components/PageConfigProvider/types';
import type { ReportRun } from '../components/Reports/types';
import { deserializePageDimensionSources } from '../utils/dimension-source-serialization';

async function fetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<T> {
  const csrfToken = getAuthDashMetaCsrfToken();

  const response = await fetch(input, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-surf-token-dash-meta': csrfToken ?? '',
      ...(init?.headers || {}),
    },
    ...init,
  });

  if (!response.ok) {
    const message = await response.text();
    const error: Error & { status?: number } = new Error(
      message || 'Unknown error'
    );
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return await response.json();
}

//
// Build a Dash Pages
//
export const getPages = async () => {
  const pages = await fetchJson<Page[]>(
    `${DASH_META_ROOT}/api/build-a-dash/pages`,
    {
      method: 'GET',
    }
  );
  return pages.map(deserializePageDimensionSources);
};

export const getPage = async (pageId: string) => {
  const page = await fetchJson<Page>(
    `${DASH_META_ROOT}/api/build-a-dash/pages/${pageId}`,
    {
      method: 'GET',
    }
  );
  return deserializePageDimensionSources(page);
};

export const createPage = (page: Page) =>
  fetchJson<Page>(`${DASH_META_ROOT}/api/build-a-dash/pages`, {
    method: 'POST',
    body: JSON.stringify(page),
  });

export const updatePage = (pageId: string, page: Partial<Page>) =>
  fetchJson<Page>(`${DASH_META_ROOT}/api/build-a-dash/pages/${pageId}`, {
    method: 'PUT',
    body: JSON.stringify(page),
  });

export const deletePage = (pageId: string) =>
  fetchJson<boolean>(`${DASH_META_ROOT}/api/build-a-dash/pages/${pageId}`, {
    method: 'DELETE',
  });

//
// Build a Dash Report Runs
//
export const createReportRun = (reportRun: Omit<ReportRun, 'id'>) =>
  fetchJson<ReportRun>(`${DASH_META_ROOT}/api/build-a-dash/report-runs`, {
    method: 'POST',
    body: JSON.stringify(reportRun),
  });

export const updateReportRun = (runId: string, reportRun: Partial<ReportRun>) =>
  fetchJson<ReportRun>(
    `${DASH_META_ROOT}/api/build-a-dash/report-runs/${runId}`,
    {
      method: 'PUT',
      body: JSON.stringify(reportRun),
    }
  );

export const deleteReportRun = (runId: string) =>
  fetchJson<void>(`${DASH_META_ROOT}/api/build-a-dash/report-runs/${runId}`, {
    method: 'DELETE',
  });

export const getReportRuns = (ownerId: string) =>
  fetchJson<Omit<ReportRun, '_id'>[]>(
    `${DASH_META_ROOT}/api/build-a-dash/report-runs/owner/${ownerId}`,
    {
      method: 'GET',
    }
  );

export const getReportRun = (runId: string) =>
  fetchJson<ReportRun>(
    `${DASH_META_ROOT}/api/build-a-dash/report-runs/${runId}`,
    {
      method: 'GET',
    }
  );
